import React, { FC } from 'react';
import { Button, useTheme } from '@mui/material';
import moment from 'moment';

import { IEmployeeBalanceDTO } from 'src/api/dtos';
import { DATE_FORMAT } from 'src/shared/constants';
import { DurationFormatEnums } from 'src/shared/enums';
import { useTranslate } from 'src/shared/providers';
import { formatDuration } from 'src/shared/utils';

import { AbsenceBalanceIcon } from './AbsenceBalanceIcon';
import './AbsenceBalanceSummary.styles.scss';

interface Props {
  balance: IEmployeeBalanceDTO;
  shouldShowError: boolean;
  shouldDisplayAsDays: boolean;
  isFutureBalanceCheckEnabled: boolean;
  onClickDetails?: () => void;
  className?: string;
}

export const AbsenceBalanceSummary: FC<Props> = ({
  balance,
  shouldShowError,
  shouldDisplayAsDays,
  isFutureBalanceCheckEnabled,
  className,
  onClickDetails,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const currentBalanceDays =
    shouldDisplayAsDays && balance.currentBalanceInDays
      ? `/ ${balance.currentBalanceInDays} ${t('global.days')}`
      : undefined;

  const expectedBalanceDays =
    shouldDisplayAsDays && balance.expectedBalanceInDays
      ? `/ ${balance.expectedBalanceInDays} ${t('global.days')}`
      : undefined;

  const date = moment(balance.date).format(DATE_FORMAT);

  return (
    <div className={className}>
      <table className={`absence-balances-summary ${theme.palette.mode}`}>
        <thead>
          <tr>
            <th scope="col">{t('absences.currentBal')}</th>
            {isFutureBalanceCheckEnabled && (
              <>
                <th scope="col">
                  {t('absences.expectedBal')} <br />({t('absences.asOfDate', { date })})
                </th>
                <th scope="col">{onClickDetails ? t('absences.breakdown') : t('absences.status')}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {formatDuration(balance.currentBalanceInSeconds, DurationFormatEnums.T_24_HOURS, 'seconds')}{' '}
              {currentBalanceDays}
            </td>
            {isFutureBalanceCheckEnabled && (
              <>
                <td className="highlight">
                  {formatDuration(balance.expectedBalanceInSeconds, DurationFormatEnums.T_24_HOURS, 'seconds')}{' '}
                  {expectedBalanceDays}
                </td>
                <td>
                  {onClickDetails ? (
                    <Button id="view-breakdown" size="small" color="secondary" onClick={onClickDetails}>
                      {t('absences.viewBreakdown')}
                    </Button>
                  ) : (
                    <AbsenceBalanceIcon
                      balance={balance}
                      shouldShowError={shouldShowError}
                      shouldShowBreakdown={false}
                      size={28}
                    />
                  )}
                </td>
              </>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};
